/**
 * Location Map
 * -------------------
 *
 * Initialises all Mapbox office location maps on the page.
 *
 * Mapbox GL JS and its stylesheet are loaded dynamically from Mapbox's CDN.
 * Before adding either asset to the DOM, the class checks whether it has
 * already been added.
 *
 * Multiple map instances can exist on the same page. Each map uses its own
 * configuration supplied through data attributes.
 *
 * Example markup:
 *
 * <div
 *     id="map-123456"
 *     data-office-location-map
 *     data-mapbox-access-token="..."
 *     data-mapbox-style-url="mapbox://styles/..."
 *     data-latitude="51.123"
 *     data-longitude="-0.123"
 * ></div>
 *
 * Usage:
 *
 * import LocationMap from './modules/LocationMap';
 *
 * new LocationMap();
 */

class LocationMap {
    constructor() {
        this.selector = '[data-location-map]';

        this.mapboxCssId = 'mapbox-gl-css';
        this.mapboxJsId = 'mapbox-gl-js';

        this.mapboxCssUrl = 'https://api.mapbox.com/mapbox-gl-js/v3.30.0/mapbox-gl.css';
        this.mapboxJsUrl = 'https://api.mapbox.com/mapbox-gl-js/v3.30.0/mapbox-gl.js';

        this.maps = new Map();

        this.init();
    }

    /**
     * Find all map elements on the page.
     */
    init() {
        const elements = document.querySelectorAll(this.selector);

        if (!elements.length) {
            return;
        }

        this.loadMapbox()
            .then(() => {
                elements.forEach((element) => {
                    this.createMap(element);
                });
            })
            .catch((error) => {
                console.error('OfficeLocationMap: Mapbox failed to load.', error);
            });
    }

    /**
     * Load the Mapbox CSS and JavaScript.
     *
     * Assets are only added if they do not already exist.
     *
     * @returns {Promise<void>}
     */
    loadMapbox() {
        return new Promise((resolve, reject) => {

            /**
             * Add Mapbox CSS if it has not already been added.
             */
            if (!document.getElementById(this.mapboxCssId)) {
                const stylesheet = document.createElement('link');

                stylesheet.id = this.mapboxCssId;
                stylesheet.rel = 'stylesheet';
                stylesheet.href = this.mapboxCssUrl;

                document.head.appendChild(stylesheet);
            }

            /**
             * Mapbox is already available globally.
             */
            if (window.mapboxgl) {
                resolve();
                return;
            }

            /**
             * Check whether the Mapbox script has already
             * been added by another map instance/module.
             */
            const existingScript = document.getElementById(this.mapboxJsId);

            if (existingScript) {

                existingScript.addEventListener(
                    'load',
                    () => resolve(),
                    { once: true }
                );

                existingScript.addEventListener(
                    'error',
                    (error) => reject(error),
                    { once: true }
                );

                return;
            }

            /**
             * Mapbox has not been loaded yet, so create
             * and append the script to the document.
             */
            const script = document.createElement('script');

            script.id = this.mapboxJsId;
            script.src = this.mapboxJsUrl;

            script.addEventListener(
                'load',
                () => resolve(),
                { once: true }
            );

            script.addEventListener(
                'error',
                (error) => reject(error),
                { once: true }
            );

            document.head.appendChild(script);
        });
    }

    /**
     * Create an individual Mapbox map.
     *
     * @param {HTMLElement} element
     */
    createMap(element) {

        /**
         * Prevent duplicate initialisation.
         */
        if (element.dataset.mapboxInitialized === 'true') {
            return;
        }


        const {
            mapboxAccessToken,
            mapboxStyleUrl,
            latitude,
            longitude,
        } = element.dataset;


        const coordinates = [
            Number(longitude),
            Number(latitude),
        ];

        /**
         * Validate the required configuration.
         */
        if (
            !mapboxAccessToken ||
            !mapboxStyleUrl ||
            coordinates.some((coordinate) => Number.isNaN(coordinate))
        ) {
            console.warn(
                'OfficeLocationMap: Missing or invalid map configuration.',
                element
            );

            return;
        }

        /**
         * Mark this element as initialised before creating
         * the map to prevent duplicate instances.
         */
        element.dataset.mapboxInitialized = 'true';

        try {

            const map = new window.mapboxgl.Map({
                accessToken: mapboxAccessToken,
                container: element,
                style: mapboxStyleUrl,
                center: coordinates,
                zoom: 14,

                dragPan: false,
                dragRotate: false,
                keyboard: false,
                scrollZoom: false,
                touchPitch: false,
                touchZoomRotate: false,
                doubleClickZoom: false,
                boxZoom: false,
                cooperativeGestures: false,
                interactive: false,

                attributionControl: false,
                logoPosition: 'top-right',
            });

            /**
             * Add Mapbox attribution.
             */
            map.addControl(
                new window.mapboxgl.AttributionControl({
                    compact: true,
                }),
                'bottom-right'
            );

            /**
             * Add the location marker once the map style
             * has finished loading.
             */
            map.on('load', () => {

                map.addSource('office-location', {
                    type: 'geojson',

                    data: {
                        type: 'FeatureCollection',

                        features: [
                            {
                                type: 'Feature',
                                properties: {},

                                geometry: {
                                    type: 'Point',
                                    coordinates,
                                },
                            },
                        ],
                    },
                });


                map.addLayer({
                    id: 'office-location-dot',
                    type: 'circle',
                    source: 'office-location',

                    paint: {
                        'circle-color': '#d71920',
                        'circle-radius': 8,
                    },
                });

            });

            /**
             * Store the map instance against the element ID.
             */
            this.maps.set(element.id, map);

        } catch (error) {

            /**
             * Allow the map to be initialised again if
             * Mapbox throws an error.
             */
            delete element.dataset.mapboxInitialized;

            console.error(
                'OfficeLocationMap: Failed to initialise map.',
                error
            );
        }
    }

    /**
     * Get a Mapbox instance by its element ID.
     *
     * @param {String} mapId
     * @returns {Object|null}
     */
    getMap(mapId) {
        return this.maps.get(mapId) || null;
    }

    /**
     * Remove all currently initialised maps.
     */
    destroy() {

        this.maps.forEach((map, mapId) => {
            map.remove();

            const element = document.getElementById(mapId);

            if (element) {
                delete element.dataset.mapboxInitialized;
            }
        });

        this.maps.clear();
    }
}

export default new LocationMap();
